/**
 * One fold engine for every `<details>` accordion on the site (FAQ, curriculum,
 * and anything added later) so a dropdown never just snaps open — it animates
 * the same way, on the same curve, everywhere it appears.
 *
 * A `<details>` element carrying a `name` attribute is exclusive per the HTML
 * spec (the browser itself closes its open siblings) — this engine intercepts
 * that native close so it animates too, instead of the sibling's content
 * vanishing instantly while the one you clicked eases open.
 */

import { DURATION, EASE_OUT, reducedMotion } from './motion';

const setupItem = (details: HTMLDetailsElement, bodySelector: string): void => {
  const summary = details.querySelector<HTMLElement>('summary');
  const body = details.querySelector<HTMLElement>(bodySelector);

  if (!summary || !body) {
    return;
  }

  let animation: Animation | null = null;

  const run = (from: number, to: number, onDone: () => void) => {
    animation?.cancel();
    body.style.overflow = 'hidden';

    animation = body.animate(
      { height: [`${from}px`, `${to}px`], opacity: [from ? 1 : 0, to ? 1 : 0] },
      { duration: DURATION.base, easing: EASE_OUT }
    );

    animation.onfinish = () => {
      animation = null;
      body.style.overflow = '';
      onDone();
    };
  };

  const expand = () => {
    details.open = true;
    run(0, body.getBoundingClientRect().height, () => undefined);
  };

  const collapse = () => {
    run(body.getBoundingClientRect().height, 0, () => {
      details.open = false;
    });
  };

  const closeSiblings = () => {
    if (!details.name) {
      return;
    }

    document
      .querySelectorAll<HTMLDetailsElement>(`details[name="${details.name}"][open]`)
      .forEach((other) => {
        if (other !== details) {
          other.dispatchEvent(new CustomEvent('gf-accordion-collapse'));
        }
      });
  };

  details.addEventListener('gf-accordion-collapse', collapse);

  summary.addEventListener('click', (event) => {
    if (reducedMotion()) {
      return;
    }

    event.preventDefault();

    if (details.open) {
      collapse();
      return;
    }

    closeSiblings();
    expand();
  });
};

/**
 * Wire up every `<details>` matching `detailsSelector` on the page.
 *
 * @param detailsSelector Selector for each accordion item, e.g. '.faq__item'.
 * @param bodySelector    Selector, relative to each item, for the panel that folds.
 */
export const setupAccordion = (detailsSelector: string, bodySelector: string): void => {
  document
    .querySelectorAll<HTMLDetailsElement>(detailsSelector)
    .forEach((details) => setupItem(details, bodySelector));
};
